/**
 * Chat Service
 * REST API interactions for buddy chat system
 */

import api from './api';

const chatService = {
  /**
   * Get message history for a chat
   * @param {number} matchId - The match ID
   * @param {number} page - Page number (default: 1)
   * @param {number} perPage - Messages per page (default: 50)
   * @returns {Promise} Message history data
   */
  async getMessages(matchId, page = 1, perPage = 50) {
    try {
      const response = await api.get(`/buddy/chat/${matchId}/messages`, {
        params: { page, per_page: perPage }
      });
      return response.data;
    } catch (error) {
      console.error('[ChatService] Error fetching messages:', error);
      throw error;
    }
  },

  /**
   * Send a message (REST fallback)
   * @param {number} matchId - The match ID
   * @param {string} content - Message content
   * @param {string} messageType - Type of message
   * @returns {Promise} Sent message data
   */
  async sendMessage(matchId, content, messageType = 'text') {
    try {
      const response = await api.post(`/buddy/chat/${matchId}/messages`, {
        content,
        message_type: messageType
      });
      return response.data;
    } catch (error) {
      console.error('[ChatService] Error sending message:', error);
      throw error;
    }
  },

  /**
   * Upload a file attachment
   * @param {number} matchId - The match ID
   * @param {File} file - File to upload
   * @param {function} onProgress - Progress callback
   * @returns {Promise} Upload result with file URL
   */
  async uploadFile(matchId, file, onProgress) {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`/buddy/chat/${matchId}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress(percentCompleted);
          }
        }
      });

      return response.data;
    } catch (error) {
      console.error('[ChatService] Error uploading file:', error);
      throw error;
    }
  },

  /**
   * Edit a message
   * @param {number} matchId - The match ID
   * @param {number} messageId - The message ID
   * @param {string} content - New message content
   * @returns {Promise} Updated message data
   */
  async editMessage(matchId, messageId, content) {
    try {
      const response = await api.put(
        `/buddy/chat/${matchId}/messages/${messageId}`,
        { content }
      );
      return response.data;
    } catch (error) {
      console.error('[ChatService] Error editing message:', error);
      throw error;
    }
  },

  /**
   * Delete a message
   * @param {number} matchId - The match ID
   * @param {number} messageId - The message ID
   * @returns {Promise} Deletion confirmation
   */
  async deleteMessage(matchId, messageId) {
    try {
      const response = await api.delete(
        `/buddy/chat/${matchId}/messages/${messageId}`
      );
      return response.data;
    } catch (error) {
      console.error('[ChatService] Error deleting message:', error);
      throw error;
    }
  },

  /**
   * Mark a single message as read
   * @param {number} matchId - The match ID
   * @param {number} messageId - The message ID
   * @returns {Promise} Confirmation
   */
  async markMessageAsRead(matchId, messageId) {
    try {
      const response = await api.post(
        `/buddy/chat/${matchId}/messages/${messageId}/read`
      );
      return response.data;
    } catch (error) {
      console.error('[ChatService] Error marking message as read:', error);
      throw error;
    }
  },

  /**
   * Mark all messages in a chat as read
   * @param {number} matchId - The match ID
   * @returns {Promise} Confirmation with count
   */
  async markAllAsRead(matchId) {
    try {
      const response = await api.post(`/buddy/chat/${matchId}/mark-all-read`);
      return response.data;
    } catch (error) {
      console.error('[ChatService] Error marking all messages as read:', error);
      throw error;
    }
  },

  /**
   * Search messages in a chat
   * @param {number} matchId - The match ID
   * @param {string} query - Search query
   * @returns {Promise} Search results
   */
  async searchMessages(matchId, query) {
    try {
      const response = await api.get(`/buddy/chat/${matchId}/search`, {
        params: { q: query }
      });
      return response.data;
    } catch (error) {
      console.error('[ChatService] Error searching messages:', error);
      throw error;
    }
  },

  /**
   * Add a reaction to a message
   * @param {number} messageId - The message ID
   * @param {string} reaction - Emoji reaction
   * @returns {Promise} Reaction data
   */
  async addReaction(messageId, reaction) {
    try {
      const response = await api.post(
        `/buddy/chat/messages/${messageId}/reactions`,
        { reaction }
      );
      return response.data;
    } catch (error) {
      console.error('[ChatService] Error adding reaction:', error);
      throw error;
    }
  },

  /**
   * Remove a reaction from a message
   * @param {number} messageId - The message ID
   * @param {number} reactionId - The reaction ID
   * @returns {Promise} Confirmation
   */
  async removeReaction(messageId, reactionId) {
    try {
      const response = await api.delete(
        `/buddy/chat/messages/${messageId}/reactions/${reactionId}`
      );
      return response.data;
    } catch (error) {
      console.error('[ChatService] Error removing reaction:', error);
      throw error;
    }
  },

  /**
   * Get chat information and statistics
   * @param {number} matchId - The match ID
   * @returns {Promise} Chat info including unread count
   */
  async getChatInfo(matchId) {
    try {
      const response = await api.get(`/buddy/chat/${matchId}/info`);
      return response.data;
    } catch (error) {
      console.error('[ChatService] Error fetching chat info:', error);
      throw error;
    }
  },

  /**
   * Get chat notifications
   * @returns {Promise} List of unread notifications
   */
  async getNotifications() {
    try {
      const response = await api.get('/buddy/chat/notifications');
      return response.data;
    } catch (error) {
      console.error('[ChatService] Error fetching notifications:', error);
      throw error;
    }
  },

  /**
   * Mark a notification as read
   * @param {number} notificationId - The notification ID
   * @returns {Promise} Confirmation
   */
  async markNotificationAsRead(notificationId) {
    try {
      const response = await api.post(
        `/buddy/chat/notifications/${notificationId}/read`
      );
      return response.data;
    } catch (error) {
      console.error('[ChatService] Error marking notification as read:', error);
      throw error;
    }
  }
};

export default chatService;
